/**
 * Shared formatting utilities for dates, prices, and text.
 *
 * These replace 20+ inline copies scattered across the codebase.
 */

/**
 * Format a price value to 2 decimal places.
 *
 * @param {number|string|null|undefined} price
 * @returns {string} Formatted price or '—'
 */
export function formatPrice(price) {
  if (price === null || price === undefined || price === '') {
    return '—'
  }
  const num = typeof price === 'string' ? parseFloat(price) : price
  if (isNaN(num)) {
    return '—'
  }
  return num.toFixed(2)
}

/**
 * Parse any timestamp-like value into a Date object.
 *
 * Handles: Firestore Timestamp (.toDate()), Date, {seconds}, string, number.
 *
 * @param {*} timestamp
 * @returns {Date|null}
 */
function parseDate(timestamp) {
  if (!timestamp) return null

  let date
  if (timestamp.toDate && typeof timestamp.toDate === 'function') {
    date = timestamp.toDate()
  } else if (timestamp instanceof Date) {
    date = timestamp
  } else if (typeof timestamp === 'object' && timestamp.seconds) {
    date = new Date(timestamp.seconds * 1000)
  } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
    date = new Date(timestamp)
  } else {
    return null
  }

  return isNaN(date.getTime()) ? null : date
}

/**
 * Format a timestamp value as a localized date string.
 *
 * @param {*} timestamp - Firestore Timestamp, Date, string, number, or {seconds}
 * @param {object} [options]
 * @param {boolean} [options.time]  - Include hour & minute
 * @param {boolean} [options.short] - Use 2-digit year
 * @param {boolean} [options.long]  - Use long month name
 * @param {string}  [options.locale] - Override locale (default 'he-IL')
 * @param {string}  [options.fallback] - Fallback for invalid (default '—')
 * @returns {string}
 */
export function formatDate(timestamp, options = {}) {
  const fallback = options.fallback ?? '—'
  const date = parseDate(timestamp)
  if (!date) return fallback

  const formatOpts = {
    day: '2-digit',
    month: options.long ? 'long' : '2-digit',
    year: options.short ? '2-digit' : 'numeric',
  }

  if (options.time) {
    formatOpts.hour = '2-digit'
    formatOpts.minute = '2-digit'
  }

  return date.toLocaleDateString(options.locale ?? 'he-IL', formatOpts)
}

/**
 * Format a timestamp with date and time (convenience wrapper).
 *
 * @param {*} timestamp
 * @param {object} [options] - Same as formatDate options (time is forced true)
 * @returns {string}
 */
export function formatDateTime(timestamp, options = {}) {
  return formatDate(timestamp, { ...options, time: true })
}

/**
 * Return a string representation of a value, or a fallback for empty values.
 *
 * @param {*} value
 * @param {string} [fallback='—']
 * @returns {string}
 */
export function safeText(value, fallback = '—') {
  if (value === null || value === undefined || value === '') {
    return fallback
  }
  return String(value)
}

/**
 * Parse any Firestore-timestamp-like value into a Date object.
 *
 * Handles: Firestore Timestamp (.toDate()), Date, {seconds, nanoseconds},
 * string, number.
 *
 * @param {*} timestamp
 * @returns {Date|null}
 */
export function parseFirestoreTimestamp(timestamp) {
  if (!timestamp) return null
  if (timestamp instanceof Date) return timestamp
  if (typeof timestamp.toDate === 'function') return timestamp.toDate()
  if (typeof timestamp.seconds === 'number') {
    return new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000)
  }
  if (typeof timestamp === 'string' || typeof timestamp === 'number') {
    const d = new Date(timestamp)
    return isNaN(d.getTime()) ? null : d
  }
  return null
}

/**
 * Normalize a phone number to Israeli +972 format.
 *
 * - Strips spaces, dashes, and any existing +972 prefix
 * - Removes leading '0'
 * - Prepends +972
 *
 * @param {string} phone
 * @returns {string}
 */
export function normalizePhoneNumber(phone) {
  if (!phone) return ''
  let cleaned = phone.replace(/[\s-]/g, '').replace(/^\+972/, '')
  if (cleaned.startsWith('0')) {
    cleaned = cleaned.substring(1)
  }
  return '+972' + cleaned
}
